import React from 'react'
import { useReducedMotion } from 'framer-motion'
import { useThemeStore } from '@/stores/themeStore'

// Smooth, eased morph between a sun and a crescent moon.
// The disc is carved into a crescent by a masked circle that slides in/out,
// while the rays scale + rotate away. All driven by CSS transitions (no JS
// animation loop) so it stays buttery and respects the GPU.
const EASE = 'cubic-bezier(0.25, 0.1, 0.25, 1)' // css "ease"
const DUR = 340 // ms — visible but snappy

// Sun ray endpoints (outside the disc, radius ~10 from center 12,12)
const RAYS: Array<[number, number, number, number]> = [
  [12, 1.5, 12, 4],
  [12, 20, 12, 22.5],
  [1.5, 12, 4, 12],
  [20, 12, 22.5, 12],
  [4.4, 4.4, 6.1, 6.1],
  [17.9, 17.9, 19.6, 19.6],
  [4.4, 19.6, 6.1, 17.9],
  [17.9, 6.1, 19.6, 4.4],
]

export const ThemeToggle: React.FC<{ className?: string }> = ({ className }) => {
  const { actualTheme, toggleTheme } = useThemeStore()
  const isDark = actualTheme === 'dark'
  const maskId = React.useId()
  // §6: honour prefers-reduced-motion — snap between states instead of
  // animating. All transition strings below interpolate this instead of the
  // raw DUR constant, so a reduced-motion user gets 0ms everywhere at once.
  const reducedMotion = useReducedMotion()
  const effectiveDur = reducedMotion ? 0 : DUR

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label="Toggle dark mode"
      onClick={toggleTheme}
      className={`theme-toggle relative inline-flex h-9 w-[52px] flex-shrink-0 items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#7170ff]/55 ${className ?? ''}`}
    >
      <span
        className="flex h-7 w-[52px] items-center rounded-full px-0.5"
        style={{
          backgroundColor: isDark ? '#34354a' : '#d4d4d8',
          transition: `background-color ${effectiveDur}ms ${EASE}`,
        }}
      >
        <span
          className="flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm"
          style={{
            transform: isDark ? 'translateX(24px)' : 'translateX(0px)',
            transition: `transform ${effectiveDur}ms ${EASE}`,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            style={{ color: isDark ? '#7170ff' : '#f5a524', transition: `color ${effectiveDur}ms ${EASE}` }}
          >
            <mask id={maskId}>
              <rect x="0" y="0" width="24" height="24" fill="white" />
              {/* Carving circle: parked off-disc for the sun, slides over the
                  disc to bite out a fat crescent for the moon. */}
              <circle
                cx="24"
                cy="2"
                r="6"
                fill="black"
                style={{
                  transform: isDark ? 'translate(-8.5px, 6.5px)' : 'translate(0px, 0px)',
                  transition: `transform ${effectiveDur}ms ${EASE}`,
                }}
              />
            </mask>

            {/* Disc — sun face / moon body. Scales up in dark so the crescent
                fills the same visual footprint as the rayed sun. */}
            <circle
              cx="12"
              cy="12"
              r="6"
              fill="currentColor"
              mask={`url(#${maskId})`}
              style={{
                transformOrigin: '12px 12px',
                transform: isDark ? 'scale(1.5)' : 'scale(1)',
                transition: `transform ${effectiveDur}ms ${EASE}`,
              }}
            />

            {/* Rays — scale + spin out when switching to moon */}
            <g
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              style={{
                transformBox: 'fill-box',
                transformOrigin: 'center',
                transform: isDark ? 'rotate(45deg) scale(0)' : 'rotate(0deg) scale(1)',
                opacity: isDark ? 0 : 1,
                transition: `transform ${effectiveDur}ms ${EASE}, opacity ${Math.round(effectiveDur * 0.7)}ms ${EASE}`,
              }}
            >
              {RAYS.map(([x1, y1, x2, y2], i) => (
                <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
              ))}
            </g>
          </svg>
        </span>
      </span>
    </button>
  )
}

export default ThemeToggle
